import { useState, useEffect, useRef } from 'react';
import { SearchClientType, SearchResultItem } from '../types';
import { multiSearchAlgolia } from '../utils/search-execution';
import { getDoctypeFilters, addLangFilter, getSearchableAttributes } from '../utils/search-filters';
import { Doctype, DOCTYPES_TO_SEARCH_INDEX } from '../types/indices';

interface UseInstantSearchProps {
   searchClient: SearchClientType;
   indices: Partial<Record<Doctype, string[]>>;
   langid?: string;
}

/**
 * Custom hook to handle the instant search logic
 * This separates the search functionality from UI rendering
 */
export function useInstantSearch({ searchClient, indices, langid = 'en' }: UseInstantSearchProps) {
   const [query, setQuery] = useState('');
   const [isSearching, setIsSearching] = useState(false);
   const [results, setResults] = useState<Record<Doctype, SearchResultItem[]>>(
      {} as Record<Doctype, SearchResultItem[]>
   );
   const [totalHits, setTotalHits] = useState<Record<Doctype, number>>(
      {} as Record<Doctype, number>
   );
   const [hasSearched, setHasSearched] = useState(false);
   const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

   useEffect(() => {
      const handleSearch = async () => {
         if (!query.trim() || !searchClient) {
            clearSearchState(setResults, setTotalHits, setIsSearching);
            setHasSearched(false);
            return;
         }

         setIsSearching(true);

         try {
            // Execute the search operation
            const { searchResults, hitCounts } = await executeSearchOperation(
               query,
               searchClient,
               indices,
               langid
            );

            setResults(searchResults);
            setTotalHits(hitCounts);
            setHasSearched(true);
         } catch (error) {
            console.error('Search error:', error);
         } finally {
            setIsSearching(false);
         }
      };

      if (searchTimeoutRef.current) {
         clearTimeout(searchTimeoutRef.current);
      }

      if (query.trim()) {
         searchTimeoutRef.current = setTimeout(handleSearch, 150);
      } else {
         clearSearchState(setResults, setTotalHits, setIsSearching);
         setHasSearched(false);
      }

      return () => {
         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
         }
      };
   }, [query, searchClient, indices, langid]);

   const hasResults = (Object.values(results) as SearchResultItem[][]).some(
      (hits: SearchResultItem[]) => hits.length > 0
   );

   return {
      query,
      setQuery,
      isSearching,
      results,
      totalHits,
      hasResults,
      hasSearched,
   };
}

/**
 * Clears search state when there's no query or search client
 */
function clearSearchState(
   setResults: (value: Record<Doctype, SearchResultItem[]>) => void,
   setTotalHits: (value: Record<Doctype, number>) => void,
   setIsSearching: (value: boolean) => void
) {
   // Initialize empty state with all doctypes
   const emptyResults: Record<Doctype, SearchResultItem[]> = {} as Record<
      Doctype,
      SearchResultItem[]
   >;
   const emptyHits: Record<Doctype, number> = {} as Record<Doctype, number>;

   const allDoctypes: Doctype[] = Object.keys(DOCTYPES_TO_SEARCH_INDEX) as Doctype[];
   allDoctypes.forEach(doctype => {
      emptyResults[doctype] = [];
      emptyHits[doctype] = 0;
   });

   setResults(emptyResults);
   setTotalHits(emptyHits);
   setIsSearching(false);
}

/**
 * Creates search parameters for a specific document type
 */
function createSearchParameters(doctype: Doctype): Record<string, unknown> {
   const searchableAttributes = getSearchableAttributes(doctype);
   const parameters: Record<string, unknown> = {};

   if (searchableAttributes?.length) {
      parameters.restrictSearchableAttributes = searchableAttributes;
   }

   // For distinct filtering (similar to doctypes.js)
   if (doctype === 'post' || doctype === 'news' || doctype === 'product') {
      parameters.distinct = true;
   }

   return parameters;
}

/**
 * Creates search promises for all document types
 */
function createSearchPromises(
   indices: Partial<Record<Doctype, string[]>>,
   searchClient: SearchClientType,
   query: string,
   langid: string
): { promises: Promise<{ hits: SearchResultItem[]; nbHits: number }>[]; doctypeKeys: Doctype[] } {
   const searchPromises: Promise<{ hits: SearchResultItem[]; nbHits: number }>[] = [];
   const doctypeKeys: Doctype[] = [];

   for (const [doctype, indexNames] of Object.entries(indices) as [
      Doctype,
      string[] | undefined,
   ][]) {
      if (!indexNames || indexNames.length === 0) continue;

      // Get doctype-specific filters
      let filters = getDoctypeFilters(doctype);
      filters = addLangFilter(filters, doctype, langid);

      const searchParameters = createSearchParameters(doctype);

      doctypeKeys.push(doctype);
      searchPromises.push(
         multiSearchAlgolia<SearchResultItem>(
            searchClient,
            indexNames,
            query,
            3, // hitsPerPage
            filters,
            searchParameters
         ).catch(error => {
            console.error(`Error searching ${doctype} indices:`, error);
            return { hits: [], nbHits: 0 };
         })
      );
   }

   return { promises: searchPromises, doctypeKeys };
}

/**
 * Processes search results and adds doctype information to hits
 */
function processSearchResults(
   searchResultsArray: { hits: SearchResultItem[]; nbHits: number }[],
   doctypeKeys: Doctype[]
): { searchResults: Record<Doctype, SearchResultItem[]>; hitCounts: Record<Doctype, number> } {
   const searchResults: Record<Doctype, SearchResultItem[]> = {} as Record<
      Doctype,
      SearchResultItem[]
   >;
   const hitCounts: Record<Doctype, number> = {} as Record<Doctype, number>;

   // Initialize all possible doctypes with empty arrays and zero counts
   const allDoctypes: Doctype[] = Object.keys(DOCTYPES_TO_SEARCH_INDEX) as Doctype[];
   allDoctypes.forEach(doctype => {
      searchResults[doctype] = [];
      hitCounts[doctype] = 0;
   });

   // Update with actual results for doctypes that were searched
   searchResultsArray.forEach((result, index) => {
      const doctype = doctypeKeys[index];
      const { hits, nbHits } = result;

      if (hits.length > 0) {
         hits.forEach(hit => {
            // Add doctype to each hit for easier identification
            hit.doctype ||= doctype;
         });
         searchResults[doctype] = hits;
         hitCounts[doctype] = nbHits;
      }
   });

   return { searchResults, hitCounts };
}

/**
 * Executes the actual search operation
 */
async function executeSearchOperation(
   query: string,
   searchClient: SearchClientType,
   indices: Partial<Record<Doctype, string[]>>,
   langid: string
): Promise<{
   searchResults: Record<Doctype, SearchResultItem[]>;
   hitCounts: Record<Doctype, number>;
}> {
   const { promises: searchPromises, doctypeKeys } = createSearchPromises(
      indices,
      searchClient,
      query,
      langid
   );

   // Wait for all search promises to resolve in parallel
   const searchResultsArray = await Promise.all(searchPromises);

   // Process the results
   return processSearchResults(searchResultsArray, doctypeKeys);
}
